// @flow
import {
  type GetClientSegmentCategoriesResponse,
  type GetClientSegmentCategoriesVariables,
  GET_CLIENT_SEGMENT_CATEGORIES,
} from 'segmented-revenue-components';
import React from 'react';
import { Query, type QueryRenderProps } from '@apollo/client/react/components';
import { ErrorMessage, NoDataMessage, LoadingSpinner } from 'client-web-app/components';

import { GET_TRANSACTION_WITH_SEGMENTS } from './queries';
import TransactionSidebar from './TransactionSidebar';
import type {
  GetTransactionsWithSegmentsData,
  GetTransactionsWithSegmentsVariables,
  DataEntry,
  JournalEntry,
  LedgerAccount,
} from './types';

function getTotal(dataEntries: Array<DataEntry>): number {
  const add = (a, b) => a + b;
  return dataEntries
    .filter(entry => entry.entryType === 'CREDIT')
    .map(entry => Math.abs(entry.amountInCents))
    .reduce(add, 0);
}

const getLedgerAccountInfo = (
  dataEntries: Array<DataEntry>,
  ledgerAccountId: ?string,
): ?LedgerAccount => {
  const matchedDataEntry = dataEntries.find(de => de.ledgerAccount.id === ledgerAccountId);
  return matchedDataEntry ? matchedDataEntry.ledgerAccount : null;
};

const formatTransactionData = (
  journalEntry: JournalEntry,
  ledgerAccountId: ?string,
): JournalEntry => ({
  id: journalEntry.id,
  credits: journalEntry.dataEntries.filter(de => de.entryType === 'CREDIT'),
  dateOfTransaction: journalEntry.dateOfTransaction,
  dataEntries: journalEntry.dataEntries,
  debits: journalEntry.dataEntries.filter(de => de.entryType === 'DEBIT'),
  description: journalEntry.vendorName,
  ledgerAccount: getLedgerAccountInfo(journalEntry.dataEntries, ledgerAccountId),
  totalAmount: getTotal(journalEntry.dataEntries),
  categorization: journalEntry.categorization,
  vendorName: journalEntry.vendorName,
});

type TransactionSidebarContainerProps = {
  attachedTo: string,
  clientId: string,
  conversationId: ?string,
  documentAttachedTo: ?string,
  documentConversationId: ?string,
  enableSegmenting: boolean,
  ledgerAccountId: ?string,
  onAllocationsRemoved: () => void,
  onFirstAllocationCreated: () => void,
  onSidebarClose: () => void,
  transactionId: string,
};

export default function TransactionSidebarContainer({
  clientId,
  conversationId,
  transactionId,
  enableSegmenting,
  ledgerAccountId,
  attachedTo,
  onAllocationsRemoved,
  onFirstAllocationCreated,
  onSidebarClose,
  documentAttachedTo,
  documentConversationId,
}: TransactionSidebarContainerProps) {
  return (
    <Query
      query={GET_TRANSACTION_WITH_SEGMENTS}
      notifyOnNetworkStatusChange
      variables={{ journalEntryId: transactionId }}
    >
      {(
        getTransactionsWithSegments: QueryRenderProps<
          GetTransactionsWithSegmentsData,
          GetTransactionsWithSegmentsVariables,
        >,
      ) => (
        <Query
          query={GET_CLIENT_SEGMENT_CATEGORIES}
          notifyOnNetworkStatusChange
          variables={{ clientId }}
        >
          {(
            getClientSegmentCategories: QueryRenderProps<
              GetClientSegmentCategoriesResponse,
              GetClientSegmentCategoriesVariables,
            >,
          ) => {
            const isLoading =
              getTransactionsWithSegments.loading || getClientSegmentCategories.loading;

            const isRefetching =
              getTransactionsWithSegments.networkStatus === 4 ||
              getClientSegmentCategories.networkStatus === 4;

            if (isLoading || isRefetching) return <LoadingSpinner />;

            if (getTransactionsWithSegments.error) {
              return (
                <ErrorMessage
                  error={getTransactionsWithSegments.error}
                  message="Sorry, we're having trouble retrieving your data."
                  minHeight={400}
                  onRetry={getTransactionsWithSegments.refetch}
                />
              );
            }

            if (getClientSegmentCategories.error) {
              return (
                <ErrorMessage
                  error={getClientSegmentCategories.error}
                  message="Sorry, we're having trouble retrieving your data."
                  minHeight={400}
                  onRetry={getClientSegmentCategories.refetch}
                />
              );
            }

            const journalEntry =
              getTransactionsWithSegments &&
              getTransactionsWithSegments.data &&
              !!getTransactionsWithSegments.data.journalEntry &&
              getTransactionsWithSegments.data.journalEntry;

            const segmentCategories =
              (getClientSegmentCategories &&
                getClientSegmentCategories.data &&
                getClientSegmentCategories.data.client &&
                getClientSegmentCategories.data.client.segmentCategories) ||
              [];

            if (!journalEntry) {
              return <NoDataMessage message="There’s no data to show." minHeight={250} />;
            }

            return (
              <TransactionSidebar
                attachedTo={attachedTo}
                clientId={clientId}
                conversationId={conversationId}
                enableSegmenting={enableSegmenting}
                ledgerAccountId={ledgerAccountId}
                onAllocationsRemoved={onAllocationsRemoved}
                onFirstAllocationCreated={onFirstAllocationCreated}
                onSidebarClose={onSidebarClose}
                documentConversationId={documentConversationId}
                documentAttachedTo={documentAttachedTo}
                segmentCategories={enableSegmenting ? segmentCategories : null}
                transaction={formatTransactionData(journalEntry, ledgerAccountId)}
              />
            );
          }}
        </Query>
      )}
    </Query>
  );
}

TransactionSidebarContainer.defaultProps = {
  conversationId: undefined,
  documentAttachedTo: undefined,
  documentConversationId: undefined,
  ledgerAccountId: undefined,
};
